import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { useSelector } from "react-redux";
import { CoreReduxState } from "../store";
import { MED_SKU } from "src/utils/medEligibility";
import { BillingUserProto } from "@noom/noom-contracts/noom_contracts/billing/user/billing_user";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import { CustomerDetailsProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/shared";
import {
  OrderOperationProto,
  SwitchOperationProto,
} from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import { MoneyProto } from "@noom/noom-contracts/noom_contracts/lib/money";
import { InvoiceProto } from "@noom/noom-contracts/noom_contracts/billing/bms/invoices/invoice";
import { PurchaseTypeEnumProto_Value } from "@noom/noom-contracts/noom_contracts/billing/shared";

export type SaleItemInfo = {
  medSku?: MED_SKU;
  saleItemId?: string;
  trialSaleItemId?: string;
  planName?: string;
  legacyNoomPlanId?: string;
  pspPlanId?: string;
};

export type CheckoutOrderEntry = {
  productName: string;
  purchaseType?: PurchaseTypeEnumProto_Value;
  saleItem?: SaleItemInfo;
  operation?: OrderOperationProto;
  order?: OrderProto;
  initialBillingCycle?: BillingCyclePreviewProto;
  recurringBillingCycle?: BillingCyclePreviewProto;
  invoice?: InvoiceProto;
};

export type PrefetchedEltv = {
  // Original (non-blended) ML ELTV. Kept for shadow comparison and as a fallback.
  eltvValue: number;
  // Blended ML ELTV. Preferred value for ad-platform optimization; falls back to
  // eltvValue when the marketing service did not return a blended value.
  eltvBlendedValue?: number | null;
};

export type CheckoutOrdersState = {
  saleItem?: SaleItemInfo;
  customerDetails?: CustomerDetailsProto;
  operation?: OrderOperationProto;
  billingUser?: BillingUserProto;
  order?: OrderProto;
  initialBillingCycle?: BillingCyclePreviewProto;
  recurringBillingCycle?: BillingCyclePreviewProto;
  refundAmount?: MoneyProto;
  hasInitializationFailed?: boolean;
  addons?: Record<string, CheckoutOrderEntry>;
  addonsCart?: string[];
  prefetchedEltv?: PrefetchedEltv | null;
};

const clearedPricing: Pick<
  CheckoutOrdersState,
  "order" | "initialBillingCycle" | "recurringBillingCycle" | "refundAmount"
> = {
  order: undefined,
  initialBillingCycle: undefined,
  recurringBillingCycle: undefined,
  refundAmount: undefined,
};

const checkoutOrdersStateSlice = createSlice({
  name: "checkoutOrdersState",
  initialState: {} as CheckoutOrdersState,
  reducers: {
    setCheckoutOrders: (state, action: PayloadAction<CheckoutOrdersState>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setPurchaseOperation: (
      state,
      action: PayloadAction<{ saleItem: SaleItemInfo; promoCode?: string }>
    ) => {
      const { saleItem, promoCode } = action.payload;
      return {
        ...state,
        ...clearedPricing,
        saleItem,
        operation: {
          purchaseOperation: {
            saleItemId: saleItem.saleItemId,
            trialSaleItemId: saleItem.trialSaleItemId,
            promoCode,
            metadata: {
              purchaseFlow: "WEB",
            },
            dependentPurchases: [],
          },
        },
      };
    },
    setSwitchOperation: (
      state,
      action: PayloadAction<{
        saleItem: SaleItemInfo;
        switchOperation: SwitchOperationProto;
      }>
    ) => {
      const { saleItem, switchOperation } = action.payload;
      return {
        ...state,
        ...clearedPricing,
        saleItem,
        operation: { switchOperation },
      };
    },
    setCheckoutCustomerDetails: (
      state,
      action: PayloadAction<CustomerDetailsProto>
    ) => {
      return {
        ...state,
        customerDetails: action.payload,
      };
    },
    setCheckoutOperationPromoCode: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      const { purchaseOperation, switchOperation } = state.operation ?? {};
      if (purchaseOperation) {
        return {
          ...state,
          operation: {
            ...state.operation,
            purchaseOperation: {
              ...purchaseOperation,
              promoCode: action.payload,
            },
          },
        };
      }
      if (switchOperation) {
        return {
          ...state,
          operation: {
            ...state.operation,
            switchOperation: { ...switchOperation, promoCode: action.payload },
          },
        };
      }
      return state;
    },
    setBillingUser: (state, action: PayloadAction<BillingUserProto>) => {
      return {
        ...state,
        billingUser: action.payload,
      };
    },
    setHasInitializationFailed: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        hasInitializationFailed: action.payload,
      };
    },
    setAddonEntries: (
      state,
      action: PayloadAction<Record<string, CheckoutOrderEntry>>
    ) => {
      return {
        ...state,
        addons: action.payload,
      };
    },
    setAddonsCartEntries: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        addonsCart: action.payload,
      };
    },
    clearAddonsCart: (state) => {
      return {
        ...state,
        addonsCart: undefined,
      };
    },
    setPrefetchedEltv: (
      state,
      action: PayloadAction<PrefetchedEltv | null | undefined>
    ) => {
      return {
        ...state,
        prefetchedEltv: action.payload,
      };
    },
  },
});

export const {
  setCheckoutOrders,
  setPurchaseOperation,
  setSwitchOperation,
  setCheckoutCustomerDetails,
  setCheckoutOperationPromoCode,
  setBillingUser,
  setHasInitializationFailed,
  setAddonEntries,
  setAddonsCartEntries,
  clearAddonsCart,
  setPrefetchedEltv,
} = checkoutOrdersStateSlice.actions;
export default checkoutOrdersStateSlice;

export function useCheckoutOrders() {
  return useSelector((state: CoreReduxState) => state.checkoutOrders);
}

export const selectAddonsCart = createSelector(
  (state: CoreReduxState) => state.checkoutOrders.addonsCart,
  (state: CoreReduxState) => state.checkoutOrders.addons,
  (addonsCart, addons) => {
    if (!addonsCart || !addons) return undefined;
    return addonsCart.map((key) => addons[key]).filter(Boolean);
  }
);

export function useAddonsCart(): CheckoutOrderEntry[] | undefined {
  return useSelector(selectAddonsCart);
}
